// Globals
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  deleteBulkProduct,
  productsSelector,
  toggleActiveBulkProduct,
  updateProduct,
  clearPublishValidationError,
} from '../../feature';
import { Resource, usePermissions } from '@/modules/Permissions';

// Components
import { Button } from '@/ui';
import { EyeIcon, CloseEyeIcon, TrashIcon } from '@/icons';
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledContentBlock,
  StyledSaveChangesButton,
  StyledActionButton,
  StyledSupplierWrapper,
  StyledSupplierBock,
  StyledSupplierName,
  StyledHeaderLeftSideBlock,
  StyledHeaderRightSideBlock,
  StyledProductUrl,
  StyledTitleModal,
  StyledButtonsWrapper,
} from './ProductDetailsMainInfoContainerStyles';
import { showErrorToast, showSuccessToast, useForm, HasUnsavedChangesModal, Modal, ModalSize } from '@/components';
import { DeleteProductForbiddenModal, ProductMainInfoForm, PublishValidationModal } from '../../components';

// API
import { ProductsAPI } from '@/api';

// Models
import { ROUTES } from '@/models/router.model';

// Helper
import { UpdateProductMainInfoDTO } from '../../dtos';
import {
  HIDDEN_CATEGORY_MESSAGE,
  IProductTabContentProps,
  ProductDetailsTabs,
  WEBSITE_PRODUCT_URL,
} from '../../helpers';
import { useHasUnsavedChangesModal, useModal } from '@/hooks';
import { isApiErrorEntity } from '@/utils';
import { ResponseStatusCodes } from '@/constants/common';

const ProductDetailsMainInfoContainer = ({
  hasUnsavedChanges,
  pendingTab,
  handleConfirmTabSwitch,
  setHasUnsavedChanges,
  clearUnsavedChanges,
  handleDeclineTabSwitch,
  onNavigateToTab,
}: IProductTabContentProps) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { id } = useParams();
  const { isOpened: isOpenForbiddenModal, openModal: openForbiddenModal, closeModal: closeForbiddenModal } = useModal();
  const { isOpened: isOpenDeleteModal, openModal: openDeleteModal, closeModal: closeDeleteModal } = useModal();
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);
  const canRegenerateName = canManageProduct;
  const { isOpened, onCloseModal, onConfirmTabSwitchAndCloseModal, handleValuesChange } = useHasUnsavedChangesModal({
    hasUnsavedChanges,
    canModalOpen: !!pendingTab,
    handleConfirmTabSwitch,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    handleDeclineTabSwitch,
    isTrackingEnabled: canManageProduct,
  });
  const [forbiddenDeleteMessage, setForbiddenDeleteMessage] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  const { isLoading, product, isPublishValidationError } = useAppState(productsSelector);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const productMainInfoDto = new UpdateProductMainInfoDTO(values);

      await dispatch(updateProduct({ id: id as string, data: productMainInfoDto })).unwrap();
      showSuccessToast({ message: t('data_saved') });
      clearUnsavedChanges();
    } catch (error) {
      showErrorToast({ message: t('product_update_validation_error') });
    }
  }, []);

  const handleSaveAndClose = async () => {
    await handleSubmit();
    onConfirmTabSwitchAndCloseModal();
  };

  const handleGenerateNamePreview = async (): Promise<Record<string, string>> => {
    try {
      const response = await ProductsAPI.generateNamePreview(id as string);

      return response.data.data;
    } catch {
      showErrorToast({ message: t('product_regenerate_name_error') });

      return {};
    }
  };

  const handleRegenerateNameClick = async () => {
    setIsRegenerating(true);

    try {
      const names = await handleGenerateNamePreview();

      if (Object.keys(names).length > 0) {
        const currentValues = form.getFieldsValue();
        const currentLocale = currentValues.locale ?? {};
        const updatedLocale = { ...currentLocale };

        for (const [locale, name] of Object.entries(names)) {
          updatedLocale[locale] = { ...updatedLocale[locale], name };
        }

        form.setFieldsValue({ locale: updatedLocale });
        handleValuesChange();
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      await dispatch(toggleActiveBulkProduct({ ids: [id as string] })).unwrap();
      showSuccessToast({ message: t('data_saved') });
    } catch (error) {
      const isCategoryHidden = isApiErrorEntity(error) && error?.message === HIDDEN_CATEGORY_MESSAGE;
      const is422Error = isApiErrorEntity(error) && error?.status === ResponseStatusCodes.UNPROCESSABLE_CONTENT;
      if (!isCategoryHidden && !is422Error) {
        showErrorToast({ message: t('product_update_validation_error') });
      }
    }
  };

  const handleClosePublishValidationModal = () => {
    dispatch(clearPublishValidationError());
  };

  const handleNavigateToRegionsAndPrices = () => {
    dispatch(clearPublishValidationError());
    onNavigateToTab(ProductDetailsTabs.REGIONS_AND_PRICES);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteBulkProduct({ ids: [id as string] })).unwrap();
      navigate(ROUTES.PRODUCTS);
      closeDeleteModal();
      showSuccessToast({ message: t('product_deleted') });
    } catch (error) {
      closeDeleteModal();
      if (isApiErrorEntity(error) && error?.status === ResponseStatusCodes.UNPROCESSABLE_CONTENT) {
        setForbiddenDeleteMessage(error.message);
        openForbiddenModal();
        return;
      } else {
        showErrorToast({ message: t('product_update_validation_error') });
      }
    }
  };

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledHeaderLeftSideBlock>
          <StyledContainerTitle>{t('main_info_container_title')}</StyledContainerTitle>
          {product?.slug && product?.active && (
            <StyledProductUrl
              href={`${WEBSITE_PRODUCT_URL}/${product?.slug}`}
              target="_blank"
              rel="noreferrer"
            >{`${WEBSITE_PRODUCT_URL}/${product?.slug}`}</StyledProductUrl>
          )}
        </StyledHeaderLeftSideBlock>
        {canManageProduct && (
          <StyledHeaderRightSideBlock>
            <StyledSaveChangesButton htmlType="submit" onClick={handleSubmit} disabled={isLoading}>
              {t('product_details_save_changes_button')}
            </StyledSaveChangesButton>
            {canRegenerateName && (
              <StyledActionButton
                onClick={handleRegenerateNameClick}
                disabled={isLoading || isRegenerating}
                loading={isRegenerating}
              >
                {t('product_regenerate_name_button')}
              </StyledActionButton>
            )}
            <StyledActionButton onClick={handleToggleActive} disabled={isLoading}>
              {product?.active ? <CloseEyeIcon /> : <EyeIcon />}
              {product?.active ? t('product_details_hide_button') : t('product_details_publish_button')}
            </StyledActionButton>
            <StyledActionButton onClick={openDeleteModal} disabled={isLoading}>
              <TrashIcon />
              {t('product_details_delete_button')}
            </StyledActionButton>
          </StyledHeaderRightSideBlock>
        )}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <StyledSupplierWrapper>
          <StyledSupplierBock>{t('main_info_supplier')}</StyledSupplierBock>
          <StyledSupplierName>{product?.store?.title}</StyledSupplierName>
        </StyledSupplierWrapper>
        <ProductMainInfoForm
          handleValuesChange={handleValuesChange}
          formInstance={form}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          initialValues={product}
          productId={product?.id}
        />
      </StyledContentBlock>
      <HasUnsavedChangesModal
        isOpened={isOpened}
        handleCloseModal={onCloseModal}
        handleSaveAndClose={handleSaveAndClose}
        handleExitAndClose={onConfirmTabSwitchAndCloseModal}
      />
      <Modal visible={isOpenDeleteModal} onCancel={closeDeleteModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('products_delete_title')}</StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={closeDeleteModal} disabled={isLoading} type="primary">
            {t('products_delete_back')}
          </Button>
          <Button onClick={handleDelete} disabled={isLoading} type="text">
            {t('products_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
      <DeleteProductForbiddenModal
        isOpenModal={isOpenForbiddenModal}
        closeModal={closeForbiddenModal}
        message={forbiddenDeleteMessage}
      />
      <PublishValidationModal
        isOpen={isPublishValidationError}
        onClose={handleClosePublishValidationModal}
        onNavigateToPrice={handleNavigateToRegionsAndPrices}
      />
    </StyledContainerWrapper>
  );
};

export default ProductDetailsMainInfoContainer;
